import { useQuery } from "@tanstack/react-query";
import { Space } from "antd";
import { useMemo } from "react";
import { createStyles } from "antd-style";
import { useUrlFilters } from "./lib/urlState/useUrlFilters";
import { useUrlPagination } from "./lib/urlState/useUrlPagination";
import { useUrlColumns } from "./lib/urlState/useUrlColumns";
import { fetchDossiers } from "@/lib/api/resources/dossiers";
import { DossiersFilterModal } from "./components/DossiersFilterModal";
import { DossiersColumnsModal } from "./components/DossiersColumnsModal";
import { DossiersExportButton } from "./components/DossiersExportButton";
import { DossierFilterValues } from "./lib/types/DossierFilterValues.interface";
import { buildDossierApiFilters } from "./lib/helpers/buildDossierApiFilters";
import {
  agentDossierColumns,
  dossierColumns,
  dossierUnreadColumn,
} from "./lib/constants/dossierColumns";
import { useCurrentUser } from "@/lib/hooks/useCurrentUser";
import { dossierFilterFields } from "./lib/constants/dossierFilterFields";
import { Table } from "@/components/v2";
import DossierFilters from "@/pages/admin/dossiers/components/dossier-filters";

const useStyles = createStyles(({ css, token }) => ({
  unreadRow: css`
    & > td:first-child {
      border-left: 4px solid ${token.colorSuccess};
    }
    &:hover > td:first-child {
      border-left-width: 6px;
    }
  `,
}));

export const DossiersList = () => {
  const { styles, cx } = useStyles();
  const { value: page, setValue: setPage } = useUrlPagination({
    param: "page",
  });

  const currentUser = useCurrentUser();

  const hasAgentsPermission = currentUser?.permissions?.includes(
    "admin_rumble_agents_nav",
  );

  const hasUnreadDossiersPermission = currentUser?.permissions?.includes(
    "admin_dossier_unread_view",
  );

  const hasExportPermission = Boolean(
    currentUser?.permissions?.includes("admin_dossier_export"),
  );

  const filterKeys = dossierFilterFields.map(
    (f) => f.name,
  ) as (keyof DossierFilterValues)[];

  const { filters, setFilters } = useUrlFilters<DossierFilterValues>({
    keys: [...filterKeys, "expression"],
    paramPrefix: "d_",
    resetPageParam: "page", // atomic page reset on filter change
  });

  const setDossierFilters = (filterValues: DossierFilterValues) => {
    setFilters({
      ...filterValues,
      archived:
        filterValues.archived === true || filterValues.archived === "true"
          ? true
          : undefined,
      expression: filters.expression,
    });
  };

  const columns = useMemo(
    () => [
      ...(hasUnreadDossiersPermission ? [dossierUnreadColumn] : []),
      ...(hasAgentsPermission ? agentDossierColumns : dossierColumns),
    ],
    [hasUnreadDossiersPermission, hasAgentsPermission],
  );

  const { visibleKeys, setVisibleKeys, persistSelection, setPersist } =
    useUrlColumns({
      allKeys: columns.map((c) => c.key as string),
      param: "cols",
      storage: { key: "dossiers.columns" },
    });

  const visibleColumns = useMemo(
    () => columns.filter((col) => visibleKeys.includes(col.key as string)),
    [columns, visibleKeys],
  );

  const dossiersQuery = useQuery({
    queryKey: ["dossiers", page, filters],
    queryFn: () =>
      fetchDossiers({
        page,
        ...buildDossierApiFilters(filters),
      }),
  });

  return (
    <div>
      <div
        style={{
          marginBottom: 16,
          display: "flex",
          justifyContent: "space-between",
          gap: 16,
          flexWrap: "wrap",
        }}
      >
        <Space size="middle" wrap>
          <DossiersFilterModal
            setDossierFilters={setDossierFilters}
            dossierFilters={filters}
          />
          <DossiersColumnsModal
            visibleKeys={visibleKeys}
            setVisibleKeys={setVisibleKeys}
            remember={persistSelection}
            setRemember={setPersist}
          />
          <DossierFilters
            dossiersQuery={dossiersQuery}
            filters={filters}
            setFilters={setFilters}
          />
        </Space>
        {hasExportPermission ? <DossiersExportButton /> : null}
      </div>
      <div>
        <Table
          dataSource={dossiersQuery.data?.member}
          columns={visibleColumns}
          scroll={{ x: "calc(" + visibleColumns.length * 100 + "px + 50%)" }}
          loading={dossiersQuery.isLoading}
          rowClassName={(record) =>
            cx(
              hasUnreadDossiersPermission && record.adminFirstReadAt === null
                ? styles.unreadRow
                : undefined,
            )
          }
          pagination={{
            pageSize: 15,
            total: dossiersQuery.data?.totalItems,
            showSizeChanger: false,
            onChange: (page) => setPage(page),
          }}
        />
      </div>
    </div>
  );
};
